import React, { useEffect, useRef } from "react";
import type { CipherText } from "@b2bportal/purchase-api";

import { logger } from "@hopper-b2b/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { B2BLoadingPopup } from "@hopper-b2b/ui";
import config from "../../../utils/config";
import {
  type PaymentEventResponse,
  UberPaymentEvent,
  type IPaymentEventSuccessResponse,
  type StartPaymentRequest,
} from "../../types";
import { BypassUberPayments } from "../BypassUberPayments";

const OPEN_ERROR_TIMEOUT = 5000; // 5 sec

export interface IPaymentScreenHandler {
  goToPrevStep: () => void;
  goToNextStep: (paymentSessionToken: string, quoteId: CipherText) => void;
  handleError: () => void;
  priceQuoteCipherText: CipherText;
}

export const PaymentScreenHandler = (props: IPaymentScreenHandler) => {
  const { t } = useI18nContext();

  const { goToPrevStep, goToNextStep, handleError, priceQuoteCipherText } =
    props;

  const uberPaymentOpenTimeout = useRef<number | undefined>();

  const openUberPayments = () => {
    const message: StartPaymentRequest = {
      type: UberPaymentEvent.START_CHECKOUT,
      payload: { quoteToken: priceQuoteCipherText.value },
    };
    window.parent.postMessage(message, config.UBER_URL);

    //Start timeout to show error if uber payments does not open after [OPEN_ERROR_TIMEOUT] seconds.

    // TODO: confirm PAYMENT_MODAL_OPENED event has been added, then uncomment
    // uberPaymentOpenTimeout.current = setTimeout(() => {
    //   handleError();
    // }, OPEN_ERROR_TIMEOUT);
    return;
  };

  const handleUberPaymentEvent = (
    event: MessageEvent<PaymentEventResponse>
  ) => {
    if (event.origin !== config.UBER_URL && config.UBER_URL !== "*") return;
    const { type } = event.data;

    //Payment events are hard to debug.
    logger().info(`PaymentEvent:${type}`);

    switch (type) {
      case UberPaymentEvent.PAYMENT_SUCCESS: {
        const {
          payload: { quoteToken, paymentSessionToken },
        } = event.data as IPaymentEventSuccessResponse;
        if (quoteToken && paymentSessionToken) {
          goToNextStep(paymentSessionToken, {
            value: quoteToken,
          } as CipherText);
        } else handleError();
        break;
      }
      case UberPaymentEvent.PAYMENT_MODAL_CLOSED:
        goToPrevStep();
        break;
      case UberPaymentEvent.PAYMENT_MODAL_OPENED:
        !!uberPaymentOpenTimeout.current &&
          clearTimeout(uberPaymentOpenTimeout.current);
        break;
    }
  };

  useEffect(() => {
    openUberPayments();
    window.addEventListener("message", handleUberPaymentEvent);

    return () => {
      window.removeEventListener("message", handleUberPaymentEvent);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {config.ENV !== "production" ? (
        <BypassUberPayments
          priceQuoteCipherText={priceQuoteCipherText}
          goToNextStep={goToNextStep}
          goToPrevStep={goToPrevStep}
        />
      ) : (
        <B2BLoadingPopup
          {...props}
          open
          fullScreen
          message={t("loading")}
          popupSize={"mobile"}
        />
      )}
    </div>
  );
};
